import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { REACT_APP_MP_URL } from '../../config/settings/environment'
import theme from '../../config/styles/theme'
import { capitalizer, formatAmount, truncate } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onComparatorTradedSectorThunk, onComparatorTradedByThunk } from './ComparatorTradedBy.actions'
import ComparatorBarChart from '../../components/Charts/ComparatorBarChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import ComparadorTradedBy from '../../components/Infographics/ComparadorTradedBy'
import { Wrapper, GridWrapper, Link } from './ComparatorTradedBy.styles'

/**
 * The ComparatorTradedBy's container.
 */
const ComparatorTradedBy = ({ firstEntity, secondEntity, latestUpdate }) => {
  const dispatch = useDispatch()
  const inputBarChartRef = useRef()
  const dataTableRef = useRef()
  const { endYear, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Montos_Transados_Rubro_Producto_${years[0].value}`)
  const { data, fetching: loading, sector } = useSelector(state => state.comparatorTradedBy)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0]?.value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onComparatorTradedByThunk({
          category: entriesValues,
          idEntity: [firstEntity.value, secondEntity.value],
          year: updatedValue
        })
      )
      setFileName(`Montos_Transados_Rubro_Producto_${updatedValue}`)
    }
  })
  const { value: entriesValues, onChange: handleChangeEnt } = useSelect({
    initialValue: sector[0]?.value,
    changeCallback: updatedValue => {
      dispatch(
        onComparatorTradedByThunk({
          category: updatedValue,
          idEntity: [firstEntity.value, secondEntity.value],
          year: yearValue
        })
      )
    },
    overwriteCallback: () => sector[0]?.value
  })
  const handleShowTable = updatedValue => setShowTable(updatedValue)

  const handleCaptureImage = () => exportToImage('ComparatorTradedBy', fileName)
  const handleDownloadFile = () => exportToCsv(data.csvStructure, fileName)

  useEffect(() => {
    dispatch(onComparatorTradedSectorThunk({ idEntity: [firstEntity.value, secondEntity.value], year: yearValue }))
  }, [firstEntity, secondEntity])

  useEffect(() => {
    if (!sector[0]?.value) {
      return
    }

    dispatch(
      onComparatorTradedByThunk({
        category: sector[0]?.value,
        idEntity: [firstEntity.value, secondEntity.value],
        year: yearValue
      })
    )
  }, [sector[0]?.value])

  return (
    <Wrapper margin="64px auto 0">
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Montos transados según rubro
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Conoce los montos asociados a los productos o servicios más transados por ambos organismos compradores según
            el año seleccionado. Los rubros a continuación corresponden a la clasificación "Nivel 1" según la ONU.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde a monto neto, sin impuestos. Recuerda
            que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href={REACT_APP_MP_URL}>www.mercadopublico.cl</Link>, corresponden a compromisos de los organismos, no
            a pagos.
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={10} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearValue} onChange={handleChangeYear} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="RUBRO" options={sector} value={entriesValues} onChange={handleChangeEnt} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            type="toggleChart"
            variant="outlined"
            singleIcon
            margin="24px 10px 0 0"
            disabled={!showTable}
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            type="toggleTable"
            variant="outlined"
            singleIcon
            disabled={showTable}
            margin="24px 0 0"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      {showTable ? (
        <GridWrapper container justifyContent="center" margin="24px 0 0">
          <GridWrapper item xs={12}>
            <DataTable
              headers={[
                { label: 'Año', value: 0 },
                { label: 'Rubro', value: 1 },
                { label: 'Organismo', value: 2 },
                { label: 'Monto', value: 3 }
              ]}
              headersCellsProps={[null, { enableSort: true }, { enableSort: true }, { enableSort: true }]}
              initialRows={6}
              loading={loading}
              records={data.csvStructure.slice(1)}
              recordsDecorators={[
                ([year]) => year,
                ([, entry]) => entry,
                ([, , entity]) => truncate(capitalizer(entity), 40),
                ([, , , amount]) => `$${formatAmount(amount)}`
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        </GridWrapper>
      ) : (
        <>
          <GridWrapper container justifyContent="center">
            <GridWrapper item xs={12}>
              <ComparatorBarChart
                categories={data.categories}
                data={data.amounts}
                distValue="amount"
                fileName={fileName}
                height={328}
                id="ComparatorTradedBy"
                inputBarChartRef={inputBarChartRef}
              />
              <GridWrapper container justifyContent="flex-end">
                <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" />
              </GridWrapper>
            </GridWrapper>
          </GridWrapper>
          <GridWrapper container justifyContent="center">
            <GridWrapper item xs={12}>
              <InfoLabel label={firstEntity.name} color={theme.palette.charts.navyBlue} margin="0 24px 0 0" />
              <InfoLabel label={secondEntity.name} color={theme.palette.charts.darkRed} />
            </GridWrapper>
          </GridWrapper>
        </>
      )}
      <GridWrapper container justifyContent="center" margin="48px 0 0">
        <GridWrapper item xs={12}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<ComparadorTradedBy />}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            latestUpdate={latestUpdate}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

ComparatorTradedBy.propTypes = {
  firstEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  secondEntity: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  }),
  latestUpdate: PropTypes.string
}

export default ComparatorTradedBy
